import {
  Box,
  Chip,
  CircularProgress,
  Divider,
  List,
  ListItem,
  Stack,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  AppPrimaryButton,
  AppPrimaryButtonOutlined,
  AppSelectWithKeys,
} from 'shared/components/AppComponents';
import LaunchIcon from '@mui/icons-material/Launch';
import { VulnerabilityService } from 'services/VulnerabilityService';
import { RiskRegisterService } from 'services/RiskRegisterService';
import { IRiskRegister } from 'modules/risk-management/risk-register/List';
import { useSelector } from 'react-redux';
import { IAppState } from 'state/RootReducer';

interface ILinkedRisksProps {
  linkedRisks: IRiskRegister[];
  vulnerabilityId: string;
  reloadDetail: () => void;
}

const LinkedRisks: React.FC<ILinkedRisksProps> = (props) => {
  const { linkedRisks, vulnerabilityId, reloadDetail } = props;
  const [isLoadingRecords, setIsLoadingRecords] = useState(false);
  const [records, setRecords] = useState<IRiskRegister[]>([]);
  const [selectedRecordId, setSelectedRecordId] = useState('');
  const [isLinking, setIsLinking] = useState('');
  const { orgACLConfig } = useSelector(
    (state: IAppState) => state.baseAppState
  );

  // API Calls
  const loadRisks = (): void => {
    setIsLoadingRecords(true);
    RiskRegisterService.getAll('')
      .then((response) => {
        const { data } = response.data;
        /* eslint no-underscore-dangle: 0 */
        setRecords(data.map((d: any) => ({ ...d, id: d._id })));
      })
      .catch((error) => {
        console.log(error);
      })
      .finally(() => {
        setIsLoadingRecords(false);
      });
  };

  const linkRecord = (payload: any, callback: any): void => {
    VulnerabilityService.linkRisk(vulnerabilityId, payload)
      .then((response) => {
        callback(response);
      })
      .catch(() => {
        callback(false);
      });
  };

  // UI Actions
  const actionOnLink = (status: string, id: string): void => {
    setIsLinking(status);
    linkRecord(
      {
        riskId: id,
        action: status,
      },
      (response: any) => {
        setIsLinking('');
        setSelectedRecordId('');
        if (typeof response === 'object') {
          reloadDetail();
        }
      }
    );
  };

  // Hooks
  useEffect(() => {
    loadRisks();
  }, []);

  // UI Elements
  return (
    <Box className="app-plain-card">
      <Box className="title">
        <Stack spacing={2} direction="row" alignItems="center">
          <img height={20} src="/images/icons/IconShield.svg" />
          <Typography variant="h4">Related Risks</Typography>
        </Stack>
        <Typography className="text-gray">
          Risk register items related to this vulnerability
        </Typography>
      </Box>

      <Box className="content">
        {isLoadingRecords && (
          <Box className="empty-content">
            <CircularProgress />
          </Box>
        )}

        {!isLoadingRecords && (
          <>
            {orgACLConfig.vulnerabilities?.includes(
              'vulnerabilities.aiVulnerabilities.risks.link'
            ) && (
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={{ xs: 1.5, sm: 4 }}
                alignItems={{ xs: 'stretch', sm: 'center' }}
                sx={{ width: '100%' }}
              >
                <AppSelectWithKeys
                  onChange={(e: any) => {
                    setSelectedRecordId(e.target.value);
                  }}
                  items={records.filter(
                    (r) => !linkedRisks.some((ls) => ls._id === r._id)
                  )}
                  titleKey="riskTitle"
                  valueKey="_id"
                />
                <AppPrimaryButtonOutlined
                  isLoading={isLinking === 'link'}
                  disabled={!selectedRecordId}
                  title="Link"
                  onClick={() => {
                    actionOnLink('link', selectedRecordId);
                  }}
                />
              </Stack>
            )}
            {linkedRisks.length > 0 && (
              <List className="app-box-border mt-4 static-box">
                {linkedRisks.map((risk, index) => (
                  <>
                    <ListItem>
                      <Stack
                        direction={{ xs: 'column', sm: 'row' }}
                        justifyContent="space-between"
                        alignItems={{ xs: 'flex-start', sm: 'center' }}
                        spacing={{ xs: 1.5, sm: 0 }}
                        sx={{ width: '100%' }}
                      >
                        <Stack>
                          <Link to={`/risk-register/detail/${risk._id}`}>
                            <Stack
                              className="text-primary mb-2"
                              alignItems="center"
                              direction="row"
                              spacing={2}
                            >
                              <LaunchIcon />
                              <Typography className="f18 fw-6">
                                {risk.riskTitle}
                              </Typography>
                            </Stack>
                          </Link>
                          <Stack direction="row" spacing={2}>
                            <Chip size="small" label={`${risk.impact}`} />
                            <Chip size="small" label={risk.likelihood} />
                            <Chip size="small" label={risk.mitigationStatus} />
                          </Stack>
                        </Stack>
                        {orgACLConfig.vulnerabilities?.includes(
                          'vulnerabilities.aiVulnerabilities.risks.unlink'
                        ) && (
                          <AppPrimaryButton
                            isLoading={selectedRecordId === risk._id}
                            onClick={() => {
                              setSelectedRecordId(`${risk._id}`);
                              actionOnLink('unlink', `${risk._id}`);
                            }}
                            className="btn-danger btn-extra-small"
                            title="Unlink"
                          />
                        )}
                      </Stack>
                    </ListItem>
                    {index < linkedRisks.length - 1 && <Divider />}
                  </>
                ))}
              </List>
            )}
            {linkedRisks.length === 0 && (
              <Box className="empty-content">
                <Typography className="f18">
                  No risks linked to this vulnerability
                </Typography>
              </Box>
            )}
          </>
        )}
      </Box>
    </Box>
  );
};

export default LinkedRisks;
